import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { buildInvitationLink } from './invitationLink';
import {
    Subtitles,
    Chat,
    pubnub,
    TextToSpeech,
    SpeechToText,
    MainWrapper,
    useStateValue,
    Select,
} from 'wwtc-sdk';
import {
    endpointSpeechToText,
    endpointTextToSpeech,
    endpointTranslate,
    endpointDetectLanguage,
    endpointUploadSubtitlesPresigned,
    signedEndpoint,
} from '../constants/connection';
import {
    Download,
    Movie,
    Desktop,
    Times,
    Users,
    User,
    Share,
    LanguageDetection,
    LogoutIcon,
} from './svg/icons';
import { handlePreRequestSubtitles } from './utils';
import buildSubtitlesFile, { buildSubtitlesTxtContent } from '../utils/subtitles';
import { uploadSubtitlesToS3 } from '../utils/uploadSubtitles';
import specialLanguages from '../constants/specialLanguages';
import notifications from '../notifications';
import {
    createModeratorWaitingRoomController,
    getSessionPresenceRole,
} from '../waitingRoom';

function Main() {
    // This values come from Login components
    // Also could set up constants values
    // This values are required like props of the MainWrapper
    const history = useHistory();
    const locationState = history.location.state || {};
    const { userName, language, gender, room, userInvite, roomType, apiToken, languages } =
        locationState;
    const isSessionHost = !userInvite;
    const [isModerator, setIsModerator] = useState(isSessionHost);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalClipboard, setModalClipboard] = useState(false);
    const [modalLanguageDetection, setModalLanguageDetection] = useState(false);
    const [waitingRoomRequests, setWaitingRoomRequests] = useState([]);
    const [waitingRoomModalVisible, setWaitingRoomModalVisible] = useState(false);
    const activeLanguageDetection = window.activeLanguageDetection;
    const activeWaitingRoom = window.activeWaitingRoom;
    const refSubtitles = useRef(null);
    const waitingRoomControllerRef = useRef(null);
    const previousWaitingCountRef = useRef(0);

    useEffect(() => {
        const applyPresenceRole = () =>
            pubnub
                .setState({
                    state: { role: getSessionPresenceRole(userInvite) },
                    channels: [room],
                })
                .catch((error) => console.error('Unable to set participant presence', error));
        const listener = {
            signal: function (s) {
                if (s.message === 'logout_admin' && !isSessionHost) {
                    pubnub.unsubscribeAll();
                    window.location.replace(window.location.origin);
                }
                if (s.message === 'clean_history_content' && !!userInvite) {
                    // clean subtitles content
                    document.getElementsByClassName('fa-trash-alt')[0].parentNode.click();
                }
            },
            status: function ({ category }) {
                if (category === 'PNConnectedCategory' || category === 'PNReconnectedCategory') {
                    applyPresenceRole();
                }
            },
        };

        setIsModerator(isSessionHost);
        pubnub.addListener(listener);
        applyPresenceRole();

        return () => pubnub.removeListener(listener);
    }, [isSessionHost, room, userInvite]);

    useEffect(() => {
        if (!activeWaitingRoom || !isSessionHost || !room) {
            setWaitingRoomRequests([]);
            setWaitingRoomModalVisible(false);
            previousWaitingCountRef.current = 0;
            return undefined;
        }

        const controller = createModeratorWaitingRoomController({
            client: pubnub,
            room,
            onQueueChange: setWaitingRoomRequests,
        });
        waitingRoomControllerRef.current = controller;
        controller.connect();

        return () => {
            controller.disconnect();
            waitingRoomControllerRef.current = null;
        };
    }, [activeWaitingRoom, isSessionHost, room]);

    useEffect(() => {
        const previousCount = previousWaitingCountRef.current;
        const nextCount = waitingRoomRequests.length;

        if (nextCount === 0) {
            setWaitingRoomModalVisible(false);
        } else if (previousCount === 0) {
            setWaitingRoomModalVisible(true);
        }

        previousWaitingCountRef.current = nextCount;
    }, [waitingRoomRequests.length]);

    const resolveWaitingRoomRequest = async (request, resolution) => {
        if (resolution === 'admit') {
            await waitingRoomControllerRef.current?.admit(request);
        } else {
            await waitingRoomControllerRef.current?.deny(request);
        }
    };

    return (
        <MainWrapper
            autologin={false}
            gender={gender}
            language={language}
            roomName={room}
            userName={userName}
            languages={languages}
            notifications={notifications}
        >
            <Header
                pubnub={pubnub}
                modalVisible={modalVisible}
                setModalVisible={setModalVisible}
                setModalClipboard={setModalClipboard}
                isSessionHost={isSessionHost}
                roomType={roomType}
                setModalLanguageDetection={setModalLanguageDetection}
                activeLanguageDetection={activeLanguageDetection}
                refSubtitles={refSubtitles}
                waitingRoomCount={waitingRoomRequests.length}
                openWaitingRoom={() => setWaitingRoomModalVisible(true)}
            />
            <WrapperComponents
                modalVisible={modalVisible}
                setModalVisible={setModalVisible}
                modalClipboard={modalClipboard}
                setModalClipboard={setModalClipboard}
                isModerator={isModerator}
                setIsModerator={setIsModerator}
                userInvite={userInvite}
                apiToken={apiToken}
                setModalLanguageDetection={setModalLanguageDetection}
                modalLanguageDetection={modalLanguageDetection}
                languages={languages}
                refSubtitles={refSubtitles}
                waitingRoomRequest={waitingRoomRequests[0] || null}
                waitingRoomCount={waitingRoomRequests.length}
                waitingRoomModalVisible={waitingRoomModalVisible}
                closeWaitingRoom={() => setWaitingRoomModalVisible(false)}
                resolveWaitingRoomRequest={resolveWaitingRoomRequest}
            />
        </MainWrapper>
    );
}

export function Header(props) {
    // initName comes from autologin component
    const {
        pubnub,
        initName,
        setModalVisible,
        setModalClipboard,
        conferenceName,
        isSessionHost,
        setModalLanguageDetection,
        activeLanguageDetection,
        refSubtitles,
        waitingRoomCount,
        openWaitingRoom,
    } = props;

    // Store references to buttons for focus management
    const shareButtonRef = useRef(null);
    const participantsButtonRef = useRef(null);
    const languageDetectionButtonRef = useRef(null);
    const waitingRoomButtonRef = useRef(null);
    const [isUploadingSubtitles, setIsUploadingSubtitles] = useState(false);

    const handleExportSubtitles = async (subtitles) => {
        const contentTxt = buildSubtitlesTxtContent(subtitles);
        const blob = new Blob([contentTxt], { type: 'text/plain;charset=utf-8' });
        const filename = `nowvideo-${conferenceName}-${new Date().toISOString().slice(0, 10)}.txt`;

        setIsUploadingSubtitles(true);
        try {
            const result = await uploadSubtitlesToS3(
                blob,
                filename,
                endpointUploadSubtitlesPresigned,
            );
            if (result.success) {
                console.log('Audit log exported successfully');
            } else {
                console.log(`Error exporting audit log: ${result.error}`);
            }
        } finally {
            setIsUploadingSubtitles(false);
        }
    };

    const handleLogout = async () => {
        // export audit log
        if (window.enableAudit) {
            const subtitles = refSubtitles.current.state.subtitles;
            await handleExportSubtitles(subtitles);
        }
        if (pubnub) {
            if (isSessionHost) {
                await pubnub.signal({
                    message: 'logout_admin',
                    channel: conferenceName,
                });
            }
            pubnub.unsubscribeAll();
            window.location.replace(window.location.origin);
        } else {
            window.location.replace(window.location.origin);
        }
    };

    const handleKeyPress = (event, callback) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            callback();
        }
    };

    const handleShareClick = () => {
        // Store the current focused element (the button that triggered the modal)
        const activeElement = document.activeElement;
        if (activeElement) {
            sessionStorage.setItem(
                'modalTriggerElementId',
                activeElement.getAttribute('data-modal-trigger') || 'share-btn',
            );
        }
        setModalClipboard(true);
        notifications.info({
            title: 'Share room opened',
            description: 'Participant sharing options are ready.',
        });
    };

    const handleParticipantsClick = () => {
        const activeElement = document.activeElement;
        if (activeElement) {
            sessionStorage.setItem(
                'modalTriggerElementId',
                activeElement.getAttribute('data-modal-trigger') || 'participants-btn',
            );
        }
        setModalVisible(true);
    };

    const handleLanguageDetectionClick = () => {
        const activeElement = document.activeElement;
        if (activeElement) {
            sessionStorage.setItem(
                'modalTriggerElementId',
                activeElement.getAttribute('data-modal-trigger') || 'lang-detection-btn',
            );
        }
        setModalLanguageDetection(true);
    };

    const handleWaitingRoomClick = () => {
        if (waitingRoomCount > 0) openWaitingRoom();
    };

    return (
        <header role="banner">
            <div className="header-left-section">
                <div className="info-user">
                    <img src={`${window.logoHeader}`} alt="WWTC" />
                </div>
            </div>
            <div className="header-center-section">
                <div className="direct-buttons hidden-mobile">
                    <div
                        role="heading"
                        aria-level="1"
                        tabIndex="0"
                        aria-label="Application: NOW Video Conferencing"
                    >
                        <span>NOW Video Conferencing</span>
                    </div>
                </div>
            </div>
            <div className="header-right-section">
                <div className="header-actions">
                    {activeLanguageDetection && (
                        <button
                            ref={languageDetectionButtonRef}
                            type="button"
                            className="lang-detection-btn"
                            data-modal-trigger="lang-detection-btn"
                            data-toggle="tooltip"
                            data-placement="bottom"
                            title="Language Detection"
                            aria-label="Language Detection"
                            aria-describedby="lang-detection-tooltip"
                            data-container="body"
                            tabIndex="0"
                            onClick={handleLanguageDetectionClick}
                            onKeyDown={(e) => handleKeyPress(e, handleLanguageDetectionClick)}
                        >
                            <LanguageDetection />
                        </button>
                    )}
                    {isSessionHost && (
                        <div className="action-group share-group">
                            <button
                                ref={shareButtonRef}
                                type="button"
                                className="share-btn"
                                data-modal-trigger="share-btn"
                                data-toggle="tooltip"
                                data-placement="bottom"
                                title="Share room"
                                aria-label="Share room"
                                aria-describedby="share-room-tooltip"
                                data-container="body"
                                tabIndex="0"
                                onClick={handleShareClick}
                                onKeyDown={(e) => handleKeyPress(e, handleShareClick)}
                            >
                                <span className="visually-hidden">Share room</span>
                                <Share />
                            </button>
                        </div>
                    )}
                    {isSessionHost && waitingRoomCount > 0 && (
                        <div className="action-group waiting-room-group">
                            <button
                                ref={waitingRoomButtonRef}
                                type="button"
                                className="waiting-room-btn"
                                title={`${waitingRoomCount} waiting for admission`}
                                aria-label={`Open waiting room. ${waitingRoomCount} waiting`}
                                onClick={handleWaitingRoomClick}
                                onKeyDown={(e) => handleKeyPress(e, handleWaitingRoomClick)}
                            >
                                <span className="waiting-room-label hidden-mobile">
                                    Waiting room
                                </span>
                                <span className="waiting-room-badge" aria-hidden="true">
                                    {waitingRoomCount}
                                </span>
                            </button>
                        </div>
                    )}
                    <div className="action-group participants-group">
                        <span className="participants-label hidden-mobile">Roll Call</span>
                        <button
                            ref={participantsButtonRef}
                            type="button"
                            className="participants-btn"
                            data-modal-trigger="participants-btn"
                            data-toggle="tooltip"
                            data-placement="bottom"
                            title="Participants"
                            aria-label="View participants"
                            aria-describedby="participants-tooltip"
                            data-container="body"
                            tabIndex="0"
                            onClick={handleParticipantsClick}
                            onKeyDown={(e) => handleKeyPress(e, handleParticipantsClick)}
                        >
                            <Users />
                        </button>
                    </div>
                    <div className="action-group user-group">
                        <span className="profile-name hidden-mobile">{initName}</span>
                        <button
                            type="button"
                            disabled={isUploadingSubtitles}
                            className={
                                isUploadingSubtitles ? 'logout-btn Button-disabled' : 'logout-btn'
                            }
                            data-toggle="tooltip"
                            data-placement="bottom"
                            title="Logout"
                            aria-label="Logout"
                            aria-describedby="logout-tooltip"
                            data-container="body"
                            aria-hidden="false"
                            tabIndex="0"
                            onClick={handleLogout}
                            onKeyDown={(e) => handleKeyPress(e, handleLogout)}
                        >
                            <LogoutIcon />
                        </button>
                    </div>
                </div>
            </div>
        </header>
    );
}

function WrapperComponents(props) {
    // values from autologin
    const jitsiContainer = 'jitsi-container';
    const widthViewport = document.getElementsByTagName('body')[0].offsetWidth;
    const isMobile = widthViewport <= 600;
    const canKickParticipants = window.canKickParticipants;

    const {
        initName,
        initLanguage,
        initGender,
        conferenceName,
        modalVisible,
        setModalVisible,
        modalClipboard,
        setModalClipboard,
        isModerator,
        setIsModerator,
        userInvite,
        apiToken,
        modalLanguageDetection,
        setModalLanguageDetection,
        languages,
        refSubtitles,
        waitingRoomRequest,
        waitingRoomCount,
        waitingRoomModalVisible,
        closeWaitingRoom,
        resolveWaitingRoomRequest,
    } = props;
    const [layout, setLayout] = useState(2);
    const [jitsi, setJitsi] = useState({});
    const [oldSubtitles, setOldSubtitles] = useState([]);
    const [fontSizeSubtitles, setFontSizeSubtitles] = useState(2);

    const [{ subtitles: subtitlesComponent }] = useStateValue();
    const [isBuildingPdf, setIsBuildingPdf] = useState(false);
    const [modalDownload, setModalDownload] = useState(false);
    const [languageDetectionEnabled, setLanguageDetectionEnabled] = useState(false);
    const [valueLanguageDetection, setValueLanguageDetection] = useState(null);
    const [microphoneQuality, setMicrophoneQuality] = useState('unknown');
    const [speechRecognitionErrors, setSpeechRecognitionErrors] = useState([]);

    const emptyJitsi = Object.keys(jitsi).length === 0 && jitsi.constructor === Object;
    useEffect(() => {
        handleAudioVideo();
        if (!emptyJitsi) {
            return () => jitsi.dispose();
        }
    }, []);

    const isSpecialLanguage = (subtitles) => {
        const specialSubtitle = specialLanguages.find((special) => {
            var regex = new RegExp(special, 'g');
            var atLeastOneMatches = subtitles.some((e) => regex.test(e.speakerLanguage));
            return atLeastOneMatches;
        });

        return !!specialSubtitle;
    };

    const downloadSubtitlesPDF = (subtitles) => {
        setIsBuildingPdf(true);
        const languageBilingual = subtitlesComponent.sourceLanguage.value;
        const contentPDF = buildSubtitlesFile(subtitles, languageBilingual);

        // detect special languages
        const subtitlesWithSpecialLanguages = isSpecialLanguage(subtitles);

        // download unicode typography
        if (subtitlesWithSpecialLanguages) {
            window.print();
            setIsBuildingPdf(false);
            return;
        }

        var docDefinition = {
            content: contentPDF,
            defaultStyle: {
                font: 'Roboto',
                fontSize: 12,
            },
        };

        window.pdfMake.createPdf(docDefinition).download(() => {
            setIsBuildingPdf(false);
        });
    };

    const fetchTextToSpeechCustom = async (endpoint, text, sourceLanguage, voice, itemTtsCall) => {
        try {
            let response = await fetch(endpoint.url.replace(/:sourceLanguage/g, sourceLanguage), {
                method: endpoint.method,
                redirect: 'follow',
                body: JSON.stringify({ text }),
            });
            let data = await response.json();
            try {
                var arrayBuffer = _base64ToArrayBuffer(data.audio);
                let blob = new Blob([arrayBuffer], { type: 'audio/wav' });
                const downloadUrl = window.webkitURL.createObjectURL(blob);
                return { downloadUrl, indexCall: itemTtsCall };
            } catch (e) {
                return {
                    indexCall: itemTtsCall,
                    error: e,
                };
            }
        } catch (err) {
            return {
                indexCall: itemTtsCall,
                error: err,
            };
        }
    };

    const _base64ToArrayBuffer = (base64) => {
        var binary_string = window.atob(base64);
        var len = binary_string.length;
        var bytes = new Uint8Array(len);
        for (var i = 0; i < len; i++) {
            bytes[i] = binary_string.charCodeAt(i);
        }
        return bytes.buffer;
    };

    const initialiseJitsi = async () => {
        const _jitsi = new window.JitsiMeetExternalAPI('curiosity.worldwidetechconnections.com', {
            parentNode: document.getElementById(jitsiContainer),
            roomName: conferenceName,
            userInfo: {
                displayName: initName,
            },
            configOverwrite: {
                enableWelcomePage: false,
                enableClosePage: true,
                startWithAudioMuted: false,
                startWithVideoMuted: false,
                hideConferenceSubject: true,
                hideConferenceTimer: true,
                prejoinPageEnabled: false,
                resolution: 720,
                constraints: {
                    video: {
                        height: {
                            ideal: 720,
                            max: 720,
                            min: 240,
                        },
                    },
                },
                videoQuality: {
                    minHeightForQualityLvl: {
                        360: 'standard',
                        720: 'high',
                    },
                },
                toolbarButtons: [
                    ...(userInvite ? [] : ['security', 'recording']),
                    'microphone',
                    'camera',
                    'closedcaptions',
                    'desktop',
                    'embedmeeting',
                    'fullscreen',
                    'highlight',
                    'participants-pane',
                    'videobackgroundblur',
                    'select-background',
                    'sharedvideo',
                    'shareaudio',
                    'fodeviceselection',
                    'hangup',
                    'profile',
                    'stats',
                    'etherpad',
                    'settings',
                    'raisehand',
                    'videoquality',
                    'filmstrip',
                    'download',
                    'help',
                    'mute-everyone',
                    'tileview',
                ],
            },
            interfaceConfigOverwrite: {
                SHOW_JITSI_WATERMARK: false,
                HIDE_DEEP_LINKING_LOGO: true,
                SHOW_BRAND_WATERMARK: false,
                SHOW_WATERMARK_FOR_GUESTS: false,
                SHOW_POWERED_BY: false,
                TOOLBAR_BUTTONS: [
                    ...(userInvite ? [] : ['security', 'recording']),
                    'microphone',
                    'camera',
                    'closedcaptions',
                    'desktop',
                    'embedmeeting',
                    'fullscreen',
                    'highlight',
                    'participants-pane',
                    'videobackgroundblur',
                    'select-background',
                    'sharedvideo',
                    'shareaudio',
                    'fodeviceselection',
                    'hangup',
                    'profile',
                    'stats',
                    'etherpad',
                    'settings',
                    'raisehand',
                    'videoquality',
                    'filmstrip',
                    'download',
                    'help',
                    'mute-everyone',
                    'tileview',
                ],
            },
        });

        _jitsi.addEventListener('participantLeft', (val) => console.log(val));
        /*_jitsi.addEventListener('screenSharingStatusChanged', (obj) => {
            if (obj.on) {
                const baseUrl = window.location.origin;
                const url = `${baseUrl}/chat?n=${initName}-screenshare&id=${conferenceName}&lng=${initLanguage}&g=${initGender}`;
                window.open(
                    url,
                    '_blank',
                    `toolbar=0,location=0,menubar=0,left=5000px,width=480,height=${window.innerHeight}`,
                );
            }
        });*/
        _jitsi.addEventListener('readyToClose', () => {
            setOldSubtitles(refSubtitles.current.state.subtitles);
            const container = document.getElementById(jitsiContainer);
            container.removeChild(container.firstChild);
            setJitsi({});
            if (!isMobile) {
                setLayout(2);
                document.getElementById('wrapper').classList.add('full-subtitles-container');
            }
        });

        _jitsi.addEventListener('participantRoleChanged', (data) => {
            if (data.role === 'moderator') {
                setIsModerator(true);
            } else {
                setIsModerator(false);
            }
        });

        setJitsi(_jitsi);
    };

    const handleLayout = () => {
        setOldSubtitles(refSubtitles.current.state.subtitles);
        document.getElementById('wrapper').classList.toggle('full-subtitles-container');
        if (layout === 1) {
            setFontSizeSubtitles(2);
            return setLayout(2);
        }
        setFontSizeSubtitles(1);
        setLayout(1);
    };

    const handleAudioVideo = async () => {
        setOldSubtitles(refSubtitles.current.state.subtitles);
        if (layout === 2) {
            document.getElementById('wrapper').classList.remove('full-subtitles-container');
            setFontSizeSubtitles(1);
            await setLayout(1);
        }
        if (layout === 1) {
            document.getElementById('wrapper').classList.add('full-subtitles-container');
        }
        initialiseJitsi();
    };

    const deleteParticipantsContent = async () => {
        try {
            await pubnub.signal({
                message: 'clean_history_content',
                channel: conferenceName,
            });
            notifications.success({
                title: 'Subtitles cleared',
                description: 'All subtitles were removed from this session.',
            });
        } catch (error) {
            notifications.error({
                title: 'Delete subtitles failed',
                description: error?.message || 'Subtitles could not be cleared. Please try again.',
            });
        }
    };

    // GA4 + Local Dashboard Tracking
    const trackMicrophoneEvent = (eventName, parameters = {}) => {
        const userDomain = window.location.hostname;
        const userId = window.manifestId || 'anonymous';

        // 1. GA4 tracking
        if (typeof window.gtag === 'function') {
            window.gtag('config', 'G-TGXLD0L6MX', {
                user_id: userId,
            });

            window.gtag('event', eventName, {
                event_category: 'Microphone_Issues',
                mic_error_type: parameters.errorType || 'unknown',
                mic_quality: microphoneQuality || 'unknown',
                user_domain: userDomain,
                room_id: window.room || 'unknown',
                user_agent: navigator.userAgent,
                timestamp: new Date().toISOString(),
                ...parameters,
            });
        }

        // 2. Store in localStorage for /mic-monitor.html dashboard
        try {
            const eventData = {
                event: eventName,
                timestamp: new Date().toISOString(),
                domain: userDomain,
                room: window.room || 'unknown',
                user: userId,
                browserName: navigator.userAgent.split(' ').pop(),
                ...parameters,
            };

            const micLogs = JSON.parse(localStorage.getItem('microphoneLogs') || '[]');
            micLogs.push(eventData);
            localStorage.setItem('microphoneLogs', JSON.stringify(micLogs.slice(-100)));
        } catch (e) {
            console.warn('Local storage failed:', e);
        }
    };

    // Speech recognition error handling
    const handleSpeechRecognitionError = (error) => {
        console.error('Speech Recognition Error:', error);
        const timestamp = new Date().toISOString();
        const errorData = {
            timestamp,
            type: error.type || 'unknown',
            message: error.message || 'Speech recognition error occurred',
            details: error,
        };

        setSpeechRecognitionErrors((prev) => [...prev.slice(-4), errorData]);

        // Track microphone error in GA4
        trackMicrophoneEvent('microphone_error', {
            errorType: error.type || 'unknown',
            errorMessage: error.message || 'Speech recognition error occurred',
            errorName: error.name || 'UnknownError',
        });
    };

    // Audio quality monitoring
    const handleAudioQualityChange = (qualityData) => {
        const quality = qualityData.level || 'unknown';
        const previousQuality = microphoneQuality;
        setMicrophoneQuality(quality);

        // Track quality changes in GA4
        trackMicrophoneEvent('microphone_quality_change', {
            errorType: 'quality_change',
            newQuality: quality,
            previousQuality: previousQuality,
            audioLevel: qualityData.level,
        });

        if (quality === 'poor') {
            console.warn('Poor audio quality detected:', qualityData);
            trackMicrophoneEvent('microphone_poor_quality', {
                errorType: 'poor_quality',
                audioLevel: qualityData.level,
            });
        }
    };

    // Microphone connectivity test
    const testMicrophoneConnectivity = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    sampleRate: 16000,
                    channelCount: 1,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });

            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const source = audioContext.createMediaStreamSource(stream);
            const analyser = audioContext.createAnalyser();

            source.connect(analyser);
            analyser.fftSize = 256;

            const dataArray = new Uint8Array(analyser.frequencyBinCount);

            return new Promise((resolve) => {
                const checkAudio = () => {
                    analyser.getByteFrequencyData(dataArray);
                    const average = dataArray.reduce((a, b) => a + b) / dataArray.length;

                    stream.getTracks().forEach((track) => track.stop());
                    audioContext.close();

                    const quality = average > 10 ? 'good' : average > 5 ? 'fair' : 'poor';
                    setMicrophoneQuality(quality);

                    // Track successful microphone test
                    trackMicrophoneEvent('microphone_test_success', {
                        errorType: 'test_success',
                        detectedQuality: quality,
                        audioLevel: average,
                    });

                    resolve({ quality, level: average });
                };

                setTimeout(checkAudio, 1000);
            });
        } catch (error) {
            console.error('Microphone test failed:', error);
            setMicrophoneQuality('error');

            // Track microphone test failure with detailed error info
            trackMicrophoneEvent('microphone_test_failed', {
                errorType: 'test_failed',
                errorName: error.name,
                errorMessage: error.message,
                permissionDenied: error.name === 'NotAllowedError',
                deviceNotFound: error.name === 'NotFoundError',
                browserSupported: !!navigator.mediaDevices,
            });

            handleSpeechRecognitionError({
                type: 'microphone_test_failed',
                message: 'Unable to access microphone',
                details: error,
            });
            return { quality: 'error', error };
        }
    }, [handleSpeechRecognitionError]);

    // Run microphone test and track session on component mount
    useEffect(() => {
        // Track session start with microphone capabilities
        trackMicrophoneEvent('microphone_session_start', {
            errorType: 'session_start',
            languageDetectionEnabled: window.activeLanguageDetection || false,
            deviceType: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent)
                ? 'mobile'
                : 'desktop',
            browserName: navigator.userAgent.split(' ').pop(),
        });

        testMicrophoneConnectivity();

        // Track session end on unmount
        return () => {
            trackMicrophoneEvent('microphone_session_end', {
                errorType: 'session_end',
                finalMicQuality: microphoneQuality,
                totalErrors: speechRecognitionErrors.length,
            });
        };
    }, [testMicrophoneConnectivity]);

    const renderSubtitles = () => {
        return (
            <Subtitles.SubtitlesUI
                ref={refSubtitles}
                username={initName}
                initialSourceLanguage={initLanguage}
                initialTargetLanguage={initLanguage} //change this to spanish-international when you want to translate to spanish by default
                initialVisible={true}
                endpoint={signedEndpoint(apiToken, endpointTranslate)}
                provider="pubnub"
                speechRecognition={true}
                ttsOption={true}
                showButtonCloseComponent={false}
                showLanguageServices={true}
                channel={conferenceName}
                defaultBilingual={true}
                showFlipButton={true}
                style={
                    layout === 1
                        ? stylesWrapper.subtitlesContainer
                        : stylesWrapper.fullSubtitlesContainer
                }
                preRequestSubtitles={handlePreRequestSubtitles}
                render={(subtitles) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                            className="a-element"
                            onClick={() => downloadSubtitlesPDF(subtitles)}
                        >
                            <Download />
                        </button>
                        {emptyJitsi ? (
                            <button className="a-element" onClick={handleLayout}>
                                <Movie />
                            </button>
                        ) : null}
                        {/* Microphone quality indicator */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: '12px',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                backgroundColor:
                                    microphoneQuality === 'good'
                                        ? '#4CAF50'
                                        : microphoneQuality === 'fair'
                                          ? '#FF9800'
                                          : microphoneQuality === 'poor'
                                            ? '#F44336'
                                            : '#9E9E9E',
                                color: 'white',
                            }}
                            title={`Microphone quality: ${microphoneQuality}`}
                        >
                            🎤 {microphoneQuality}
                        </div>
                        {/* Error indicator */}
                        {speechRecognitionErrors.length > 0 && (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontSize: '12px',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    backgroundColor: '#F44336',
                                    color: 'white',
                                    cursor: 'pointer',
                                }}
                                title={`${speechRecognitionErrors.length} recent errors. Click to retry microphone test.`}
                                onClick={testMicrophoneConnectivity}
                            >
                                ⚠️ {speechRecognitionErrors.length}
                            </div>
                        )}
                    </div>
                )}
                subtitlesParent={oldSubtitles}
                fontSizeSubtitles={fontSizeSubtitles}
                bilingualTextColor={window.bilingualTextColor}
            />
        );
    };

    const renderJitsiFrame = () => {
        return (
            <div
                id="jitsi-container"
                style={
                    layout === 1
                        ? stylesWrapper.fullSubtitlesContainer
                        : stylesWrapper.subtitlesContainer
                }
            >
                {emptyJitsi ? (
                    <div
                        style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <button
                            className="Button Button-primary"
                            onClick={handleAudioVideo}
                            style={{ padding: '0.9em 1em' }}
                        >
                            <div style={{ display: 'block', marginBottom: '.5em' }}>
                                <Desktop />
                            </div>
                            <p style={{ marginBottom: 0, textTransform: 'capitalize' }}>
                                enable audio/video
                            </p>
                        </button>
                    </div>
                ) : null}
            </div>
        );
    };

    const kickOutParticipant = async (participant) => {
        await pubnub.signal({
            message: `kickout_participant-${participant.id}`,
            channel: conferenceName,
        });
    };

    const subtitlesRendered = renderSubtitles();
    return (
        <div className="wrapper-components" id="wrapper">
            <div className="wrapper-left">
                <div className="loading-jitsi">
                    <p>Loading audio/video...</p>
                </div>
                {layout === 1 ? renderJitsiFrame() : subtitlesRendered}
            </div>
            <div className="wrapper-right">
                <Chat.ChatUI
                    attachFile={true}
                    channel={conferenceName}
                    endpoint={signedEndpoint(apiToken, endpointTranslate)}
                    fontSizeChat={1}
                    initialSourceLanguage={initLanguage}
                    initialTargetLanguage={initLanguage}
                    initialVisible={true}
                    provider="pubnub"
                    style={stylesWrapper.chatContainer}
                    showButtonCloseComponent={false}
                    speechRecognition={true}
                    username={initName}
                />
                {layout === 1 ? subtitlesRendered : renderJitsiFrame()}
            </div>
            <SpeechToText.SpeechToTextUI
                username={initName}
                initialLanguage={initLanguage}
                initialVisible={false}
                initialActive={false}
                withUI={false} // to use only speech recognition
                endpoint={signedEndpoint(apiToken, endpointSpeechToText)}
                detectLanguage={languageDetectionEnabled}
                endpointDetectLanguage={signedEndpoint(apiToken, endpointDetectLanguage)}
                paramsDetectLanguage={[initLanguage, valueLanguageDetection?.value]}
                audioConfig={{
                    sampleRate: 16000,
                    channels: 1,
                    format: 'wav',
                    bitrate: 128000,
                }}
                qualitySettings={{
                    noiseReduction: true,
                    audioGainControl: true,
                    echoCancellation: true,
                    autoGainControl: true,
                }}
                onError={handleSpeechRecognitionError}
                onQualityChange={handleAudioQualityChange}
            />
            <TextToSpeech.TextToSpeechUI
                initialVendor="readspeaker"
                initialLanguage={initLanguage}
                initialGender={initGender}
                initialVisible={false}
                initialActive={false}
                withUI={false} // to use only audio player
                endpoint={signedEndpoint(apiToken, endpointTextToSpeech)}
                fetchCustom={fetchTextToSpeechCustom}
            />
            {modalVisible ? (
                <ModalParticipants
                    closeModal={() => setModalVisible(false)}
                    pubnub={pubnub}
                    conferenceName={conferenceName}
                    kickOutParticipant={kickOutParticipant}
                    deleteParticipantsContent={deleteParticipantsContent}
                    isModerator={isModerator}
                    canKickParticipants={canKickParticipants}
                />
            ) : null}
            {modalClipboard ? (
                <ModalClipboard
                    closeModal={() => setModalClipboard(false)}
                    pubnub={pubnub}
                    conferenceName={conferenceName}
                />
            ) : null}
            {waitingRoomModalVisible && waitingRoomRequest ? (
                <ModalJoinRoom
                    closeModal={closeWaitingRoom}
                    waitingRoomRequest={waitingRoomRequest}
                    waitingRoomCount={waitingRoomCount}
                    resolveWaitingRoomRequest={resolveWaitingRoomRequest}
                />
            ) : null}
            {isBuildingPdf ? <ModalDownload onCloseModal={() => setModalDownload(false)} /> : null}
            {modalLanguageDetection ? (
                <ModalLanguageDetection
                    closeModal={() => setModalLanguageDetection(false)}
                    pubnub={pubnub}
                    conferenceName={conferenceName}
                    languages={languages}
                    kickOutParticipant={kickOutParticipant}
                    deleteParticipantsContent={deleteParticipantsContent}
                    languageDetectionEnabled={languageDetectionEnabled}
                    setLanguageDetectionEnabled={setLanguageDetectionEnabled}
                    valueLanguageDetection={valueLanguageDetection}
                    setValueLanguageDetection={setValueLanguageDetection}
                />
            ) : null}
        </div>
    );
}

const ModalParticipants = (props) => {
    const {
        closeModal,
        pubnub,
        conferenceName,
        kickOutParticipant,
        deleteParticipantsContent,
        isModerator,
        canKickParticipants,
    } = props;
    const [participants, setParticipants] = useState([]);
    const myPubnubId = pubnub.userId;
    const modalRef = useRef(null);

    const handleClose = () => {
        closeModal();
        // Return focus to the element that opened the modal
        setTimeout(() => {
            const triggerElementId = sessionStorage.getItem('modalTriggerElementId');
            if (triggerElementId) {
                const triggerElement = document.querySelector(
                    `[data-modal-trigger="${triggerElementId}"]`,
                );
                if (triggerElement && typeof triggerElement.focus === 'function') {
                    triggerElement.focus();
                    console.log('Focus returned to:', triggerElementId); // Debug log
                } else {
                    console.log('Could not find trigger element:', triggerElementId); // Debug log
                }
                sessionStorage.removeItem('modalTriggerElementId');
            }
        }, 150);
    };

    useEffect(() => {
        // Focus on the title first
        const timer = setTimeout(() => {
            const title = modalRef.current?.querySelector('#participants-title');
            if (title) {
                title.focus();
            }
        }, 100);

        // Handle Escape key and focus trap
        const handleKeydown = (event) => {
            if (event.key === 'Escape') {
                handleClose();
                return;
            }

            // Focus trap logic with custom order
            if (event.key === 'Tab') {
                event.preventDefault();

                // Define the correct tab order manually
                const orderedSelectors = [
                    '#participants-title',
                    'div[aria-live="polite"]',
                    'li.participant-item',
                    'button[aria-label="Download participants list"]',
                    'button.btn-success', // Include even if disabled
                    'button[aria-label="Close participants dialog"]',
                ];

                let allElements = [];
                orderedSelectors.forEach((selector) => {
                    const elements = modalRef.current?.querySelectorAll(selector);
                    if (elements) {
                        allElements.push(...Array.from(elements));
                    }
                });

                const currentIndex = allElements.indexOf(document.activeElement);
                let nextIndex;

                if (event.shiftKey) {
                    // Shift+Tab (backward)
                    nextIndex = currentIndex <= 0 ? allElements.length - 1 : currentIndex - 1;
                } else {
                    // Tab (forward)
                    nextIndex = currentIndex >= allElements.length - 1 ? 0 : currentIndex + 1;
                }

                // Skip disabled elements
                let attempts = 0;
                while (
                    allElements[nextIndex] &&
                    allElements[nextIndex].disabled &&
                    attempts < allElements.length
                ) {
                    if (event.shiftKey) {
                        nextIndex = nextIndex <= 0 ? allElements.length - 1 : nextIndex - 1;
                    } else {
                        nextIndex = nextIndex >= allElements.length - 1 ? 0 : nextIndex + 1;
                    }
                    attempts++;
                }

                if (allElements[nextIndex] && !allElements[nextIndex].disabled) {
                    allElements[nextIndex].focus();
                }
            }
        };

        document.addEventListener('keydown', handleKeydown);

        return () => {
            clearTimeout(timer);
            document.removeEventListener('keydown', handleKeydown);
        };
    }, []);

    useEffect(() => {
        pubnub
            .hereNow({
                channels: [conferenceName],
                includeState: true,
            })
            .then(async (res) => {
                let participants = [];
                const participantsInChannel = res.channels[conferenceName].occupants;

                for (let index = 0; index < participantsInChannel.length; index++) {
                    const item = participantsInChannel[index];
                    try {
                        const { data } = await pubnub.objects.getUUIDMetadata({
                            uuid: item.uuid,
                        });
                        participants.push(data);
                    } catch (e) {
                        console.log(e);
                    }
                }
                setParticipants(participants);
            });
    }, []);

    const handleParticipantKickOut = (participant) => {
        kickOutParticipant(participant);
        const filteredParticipants = participants.filter((e) => e.id !== participant.id);

        setParticipants(filteredParticipants);
    };

    const downloadParticipants = () => {
        var contentPDF = [];
        contentPDF.push({
            text: `${conferenceName} - Roll Call`,
            style: 'header',
            bold: true,
        });
        participants.forEach((item, index) => {
            contentPDF.push({
                ul: [{ text: item.name, bold: false }],
            });
        });

        var docDefinition = {
            content: contentPDF,
            defaultStyle: {
                fontSize: 12,
            },
        };
        window.pdfMake.createPdf(docDefinition).download('rollCallWWTC.pdf');
    };

    return (
        <div
            style={stylesWrapper.containerModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="participants-title"
            ref={modalRef}
            tabIndex="-1"
        >
            <div style={stylesWrapper.wrapperModal}>
                <div className="header-container">
                    <h2
                        className="header-title"
                        id="participants-title"
                        tabIndex="0"
                        style={{ margin: 0, fontSize: 'inherit' }}
                    >
                        Roll Call
                    </h2>
                    <div className="header-options">
                        <button
                            className="a-element"
                            onClick={downloadParticipants}
                            aria-label="Download participants list"
                            aria-describedby="download-participants-tooltip"
                            tabIndex="0"
                        >
                            <Download />
                        </button>
                        <button
                            className="a-element"
                            onClick={handleClose}
                            aria-label="Close participants dialog"
                            aria-describedby="close-participants-tooltip"
                            tabIndex="0"
                        >
                            <Times />
                        </button>
                    </div>
                </div>
                <div className="body-container" style={stylesWrapper.bodyModal} role="document">
                    <div tabIndex="0" aria-live="polite" style={{ marginBottom: 10 }}>
                        <p>Participants in this session ({participants.length}):</p>
                    </div>
                    <ul aria-label="List of participants">
                        {participants.map((item, index) => {
                            return (
                                <li
                                    key={index}
                                    style={stylesWrapper.participantItem}
                                    className="participant-item"
                                    tabIndex="0"
                                    aria-label={`Participant: ${item.name}${
                                        myPubnubId === item.id ? ' (You)' : ''
                                    }`}
                                >
                                    <span style={stylesWrapper.descriptionParticipant}>
                                        <span aria-hidden="false">
                                            <User />
                                        </span>
                                        &nbsp;&nbsp;
                                        <p style={{ margin: 0, textIndent: 0 }}>
                                            {item.name}
                                            {myPubnubId === item.id ? ' (You)' : null}
                                        </p>
                                    </span>
                                    {myPubnubId !== item.id && canKickParticipants ? (
                                        <button
                                            className="a-element"
                                            onClick={() => handleParticipantKickOut(item)}
                                            aria-label={`Remove ${item.name} from session`}
                                            aria-describedby="remove-participant-tooltip"
                                            tabIndex="0"
                                        >
                                            <Times />
                                        </button>
                                    ) : null}
                                </li>
                            );
                        })}
                    </ul>
                </div>
                <hr />
                {isModerator && (
                    <div
                        className="footer-container d-flex justify-content-center align-items-center"
                        styles={stylesWrapper.footerModal}
                    >
                        <button
                            className=" btn btn-success btn-sm"
                            disabled={participants.length === 1}
                            onClick={deleteParticipantsContent}
                            tabIndex="0"
                            aria-label="Delete all participants' chat content and subtitles from this session"
                            aria-describedby="delete-content-tooltip"
                        >
                            Delete participants content
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

const ModalLanguageDetection = (props) => {
    const {
        closeModal,
        languages,
        languageDetectionEnabled,
        setLanguageDetectionEnabled,
        valueLanguageDetection,
        setValueLanguageDetection,
    } = props;

    const modalRef = useRef(null);

    const handleOkModal = () => {
        if (!!languageDetectionEnabled && !valueLanguageDetection) {
            notifications.error({
                title: 'Secondary language required',
                description: 'Select a secondary language before enabling language detection.',
            });
            return;
        }

        if (!languageDetectionEnabled) {
            setValueLanguageDetection(null);
            closeModal();
        }

        if (!!languageDetectionEnabled && !!valueLanguageDetection) {
            closeModal();
        }
    };

    const handleCloseModal = () => {
        if (!!languageDetectionEnabled && !valueLanguageDetection) {
            setValueLanguageDetection(null);
            setLanguageDetectionEnabled(false);
            closeModal();
            return;
        }

        if (!languageDetectionEnabled) {
            setValueLanguageDetection(null);
            closeModal();
        }

        if (!!languageDetectionEnabled && !!valueLanguageDetection) {
            closeModal();
        }
    };

    useEffect(() => {
        // Focus on the title first
        const timer = setTimeout(() => {
            const title = modalRef.current?.querySelector('#language-detection-title');
            if (title) {
                title.focus();
            }
        }, 100);

        // Handle Escape key and focus trap
        const handleKeydown = (event) => {
            if (event.key === 'Escape') {
                handleCloseModal();
                return;
            }

            // Focus trap logic with custom order
            if (event.key === 'Tab') {
                event.preventDefault();

                // Define the correct tab order manually
                const orderedSelectors = [
                    '#language-detection-title',
                    'input[type="radio"]',
                    '.css-1pahdxg-control', // Select component
                    'button.btn-success',
                    'button[aria-label="Close language detection dialog"]',
                ];

                let allElements = [];
                orderedSelectors.forEach((selector) => {
                    const elements = modalRef.current?.querySelectorAll(selector);
                    if (elements) {
                        allElements.push(...Array.from(elements));
                    }
                });

                const currentIndex = allElements.indexOf(document.activeElement);
                let nextIndex;

                if (event.shiftKey) {
                    // Shift+Tab (backward)
                    nextIndex = currentIndex <= 0 ? allElements.length - 1 : currentIndex - 1;
                } else {
                    // Tab (forward)
                    nextIndex = currentIndex >= allElements.length - 1 ? 0 : currentIndex + 1;
                }

                if (allElements[nextIndex]) {
                    allElements[nextIndex].focus();
                }
            }
        };

        document.addEventListener('keydown', handleKeydown);

        return () => {
            clearTimeout(timer);
            document.removeEventListener('keydown', handleKeydown);
        };
    }, []);

    return (
        <div
            style={stylesWrapper.containerModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="language-detection-title"
            tabIndex="-1"
            ref={modalRef}
        >
            <div style={{ ...stylesWrapper.wrapperModal, height: 250 }}>
                <div className="header-container">
                    <div className="header-title">
                        <h2
                            id="language-detection-title"
                            className="header-title"
                            tabIndex="0"
                            style={{ margin: 0, fontSize: 14 }}
                        >
                            Language Detection
                        </h2>
                    </div>
                    <div className="header-options">
                        <button
                            className="a-element"
                            onClick={handleCloseModal}
                            aria-label="Close language detection dialog"
                            aria-describedby="close-lang-detection-tooltip"
                            tabIndex="0"
                        >
                            <Times />
                        </button>
                    </div>
                </div>
                <div className="body-container" style={stylesWrapper.bodyModal}>
                    <div tabIndex="0" style={{ marginBottom: 15 }}>
                        <p style={{ fontSize: 14, textAlign: 'center' }}>
                            Configure automatic language detection for speech recognition
                        </p>
                    </div>
                    <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
                        <legend className="visually-hidden">Language detection options</legend>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-evenly',
                                alignItems: 'center',
                            }}
                        >
                            <div>
                                <input
                                    type="radio"
                                    id="disabled"
                                    name="languageDetectionEnabled"
                                    value="disabled"
                                    checked={languageDetectionEnabled === false}
                                    onChange={(e) => setLanguageDetectionEnabled(false)}
                                    tabIndex="0"
                                />
                                <label htmlFor="disabled" style={{ fontSize: 14 }}>
                                    Disabled
                                </label>
                            </div>

                            <div>
                                <input
                                    type="radio"
                                    id="enabled"
                                    name="languageDetectionEnabled"
                                    value="enabled"
                                    checked={languageDetectionEnabled === true}
                                    onChange={(e) => setLanguageDetectionEnabled(true)}
                                    tabIndex="0"
                                />
                                <label htmlFor="enabled" style={{ fontSize: 14 }}>
                                    Enabled
                                </label>
                            </div>
                        </div>
                    </fieldset>
                    {languageDetectionEnabled && (
                        <div style={{ marginTop: 20 }}>
                            <label
                                htmlFor="language-select"
                                style={{ fontSize: 14, display: 'block', marginBottom: 8 }}
                            >
                                Select your secondary language
                            </label>
                            <Select
                                id="language-select"
                                handleChange={(lng) => setValueLanguageDetection(lng)}
                                placeholder="Select language"
                                data={languages}
                                value={valueLanguageDetection || ''}
                                showLanguageServices={true}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                aria-label="Select secondary language for detection"
                            />
                        </div>
                    )}
                </div>
                <hr />
                <div
                    className="footer-container d-flex justify-content-center align-items-center"
                    styles={stylesWrapper.footerModal}
                >
                    <button
                        onClick={handleOkModal}
                        className="btn btn-success btn-sm"
                        tabIndex="0"
                        aria-label="Apply language detection settings"
                        aria-describedby="apply-lang-detection-tooltip"
                    >
                        Ok
                    </button>
                </div>
            </div>
        </div>
    );
};

export const ModalJoinRoom = ({
    closeModal,
    waitingRoomRequest,
    waitingRoomCount,
    resolveWaitingRoomRequest,
}) => {
    const { requestId, displayName } = waitingRoomRequest;
    const [resolving, setResolving] = useState(null);
    const [resolutionError, setResolutionError] = useState(null);

    useEffect(() => {
        setResolving(null);
        setResolutionError(null);
    }, [requestId]);

    const handleResolution = async (resolution) => {
        setResolving(resolution);
        setResolutionError(null);
        try {
            await resolveWaitingRoomRequest(waitingRoomRequest, resolution);
        } catch (error) {
            setResolving(null);
            setResolutionError(`Unable to ${resolution} this participant. Please try again.`);
        }
    };

    return (
        <div
            style={stylesWrapper.containerModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="join-room-title"
        >
            <div style={{ ...stylesWrapper.wrapperModal, height: 230 }}>
                <div className="header-container">
                    <div className="header-title">
                        <span id="join-room-title">Join participant</span>
                    </div>
                    <div className="header-options">
                        <button
                            className="a-element"
                            onClick={closeModal}
                            aria-label="Close waiting room"
                        >
                            <Times />
                        </button>
                    </div>
                </div>
                <div className="body-container text-center" style={stylesWrapper.bodyModal}>
                    <p>
                        <strong>{displayName}</strong> would like to join the room
                    </p>
                    <p aria-live="polite">
                        {waitingRoomCount} {waitingRoomCount === 1 ? 'person is' : 'people are'}{' '}
                        waiting
                    </p>
                    {resolutionError ? (
                        <p role="alert" style={{ color: '#b42318' }}>
                            {resolutionError}
                        </p>
                    ) : null}
                </div>
                <div
                    className="footer-container d-flex justify-content-center align-items-center"
                    styles={stylesWrapper.footerModal}
                >
                    <button
                        className="btn btn-deny"
                        disabled={!!resolving}
                        onClick={() => handleResolution('deny')}
                        style={{ marginRight: 12 }}
                    >
                        {resolving === 'deny' ? 'Denying...' : 'Deny'}
                    </button>
                    <button
                        className="btn btn-primary"
                        disabled={!!resolving}
                        onClick={() => handleResolution('admit')}
                    >
                        {resolving === 'admit' ? 'Admitting...' : 'Admit to Session'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export const ModalClipboard = (props) => {
    const { closeModal, conferenceName } = props;
    const [isCopied, setIsCopied] = useState(false);
    const [isQrCopied, setIsQrCopied] = useState(false);

    const link = buildInvitationLink({ room: conferenceName });

    const copyLinkToClipboard = () => {
        // Create a "hidden" input
        var aux = document.createElement('input');
        try {
            aux.setAttribute('value', link);
            document.body.appendChild(aux);
            aux.select();

            const copied = document.execCommand('copy');
            if (!copied) {
                throw new Error('Clipboard copy failed');
            }

            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
            notifications.success({
                title: 'Share link copied',
                description: 'Participant link copied to the clipboard.',
            });
        } catch (error) {
            notifications.error({
                title: 'Share link copy failed',
                description: 'Copy the participant link manually and try again.',
            });
        } finally {
            if (aux.parentNode) {
                aux.parentNode.removeChild(aux);
            }
        }
    };

    const copyQrToClipboard = (elementId) => {
        const canvas = document.getElementById(elementId);
        if (!canvas) {
            notifications.error({
                title: 'QR copy failed',
                description: 'QR image is not available.',
            });
            return;
        }

        canvas.toBlob(async function (blob) {
            try {
                if (!blob || !window.ClipboardItem || !navigator.clipboard?.write) {
                    throw new Error('Image clipboard not supported');
                }

                const item = new ClipboardItem({ 'image/png': blob });
                await navigator.clipboard.write([item]);
                setIsQrCopied(true);
                setTimeout(() => setIsQrCopied(false), 2000);
                notifications.success({
                    title: 'QR copied',
                    description: 'Participant QR code copied to the clipboard.',
                });
            } catch (error) {
                notifications.error({
                    title: 'QR copy failed',
                    description: 'Copy failed. Try again or use the participant link.',
                });
            }
        });
    };

    return (
        <div style={stylesWrapper.containerModal}>
            <div style={{ ...stylesWrapper.wrapperModal, height: 350, width: 450 }}>
                <div className="header-container">
                    <div className="header-title">
                        <span>Share room</span>
                    </div>
                    <div className="header-options">
                        <button className="a-element" onClick={closeModal}>
                            <Times />
                        </button>
                    </div>
                </div>
                <div
                    className="body-container"
                    style={{ ...stylesWrapper.bodyModalInvite, textAlign: 'center' }}
                >
                    <p>Share this room with your participants</p>
                    <div style={{ marginTop: 20 }}>
                        <QRCodeCanvas value={link} size={128} id="canvas-share-room" />
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '10px',
                            marginTop: 20,
                        }}
                    >
                        <button
                            type="button"
                            className="Button  Button-primary"
                            style={{ textTransform: 'capitalize' }}
                            onClick={copyLinkToClipboard}
                        >
                            {isCopied ? 'Link Copied!' : 'Copy Link'}
                        </button>
                        <button
                            type="button"
                            className="Button  Button-secondary"
                            style={{ textTransform: 'capitalize' }}
                            onClick={() => copyQrToClipboard('canvas-share-room')}
                        >
                            {isQrCopied ? 'QR Copied!' : 'Copy QR'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ModalDownload = ({ onCloseModal }) => {
    return (
        <div style={stylesWrapper.containerModal}>
            <div style={{ ...stylesWrapper.wrapperModal, height: 180, width: 450 }}>
                <div className="header-container">
                    <div className="header-title">
                        <span>PDF Subtitles</span>
                    </div>
                    <div className="header-options"></div>
                </div>
                <div
                    className="body-container"
                    style={{ ...stylesWrapper.bodyModal, textAlign: 'center' }}
                >
                    <p>Downloading PDF file...</p>
                    <p style={{ fontSize: 14 }}>Please wait, this could take a few seconds.</p>
                </div>
            </div>
        </div>
    );
};

const stylesWrapper = {
    fullSubtitlesContainer: {
        width: 'calc(100% - 10px)',
        height: 'calc(100% - 10px)',
        margin: '5px auto',
        position: 'relative',
        zIndex: 20,
    },
    subtitlesContainer: {
        width: 'calc(100% - 10px)',
        height: 'calc(50% - 10px)',
        margin: '5px auto',
    },
    chatContainer: {
        width: 'calc(100% - 10px)',
        height: 'calc(50% - 10px)',
        margin: '5px auto',
    },
    containerModal: {
        display: 'flex',
        justifyContent: 'center',
        height: 'calc(100vh - 50px)',
        width: '100vw',
        background: 'rgba(1,1,1,0.3)',
        position: 'absolute',
        left: 0,
        top: 0,
        zIndex: 2000,
    },
    bodyModal: {
        marginTop: 24,
        height: 'calc(100% - 90px)',
        overflow: 'auto',
    },
    bodyModalInvite: {
        marginTop: 24,
        height: 'calc(100% - 30px)',
        overflow: 'auto',
    },
    wrapperModal: {
        padding: '10px',
        backgroundColor: 'white',
        borderRadius: 5,
        height: 500,
        top: 'calc(50% - 250px)',
        width: 320,
        position: 'absolute',
        WebkitBoxShadow: '4px 4px 9px -1px rgba(0,0,0,0.75)',
        MozBoxShadow: '4px 4px 9px -1px rgba(0,0,0,0.75)',
        boxShadow: '4px 4px 9px -1px rgba(0,0,0,0.75)',
    },
    participantItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 1em',
        cursor: 'default',
    },
    descriptionParticipant: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
};

export default Main;
